import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VolunteerPage() {
  const { t } = useTranslation();
  
  const volunteerAreas = [
    { key: 'foodPantry', label: t('volunteer.areas.foodPantry') },
    { key: 'worshipTeam', label: t('volunteer.areas.worshipTeam') },
    { key: 'childrensMinistry', label: t('volunteer.areas.childrensMinistry') },
    { key: 'youthMinistry', label: t('volunteer.areas.youthMinistry') },
    { key: 'outreachEvents', label: t('volunteer.areas.outreachEvents') },
    { key: 'administration', label: t('volunteer.areas.administration') },
    { key: 'prayerTeam', label: t('volunteer.areas.prayerTeam') },
    { key: 'mediaTech', label: t('volunteer.areas.mediaTech') },
    { key: 'businessTeacher', label: t('volunteer.areas.businessTeacher') },
    { key: 'counselling', label: t('volunteer.areas.counselling') },
    { key: 'farmWorker', label: t('volunteer.areas.farmWorker') },
    { key: 'maintenanceCleaning', label: t('volunteer.areas.maintenanceCleaning') },
  ];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interests: [],
    availability: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInterestChange = (area) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(area)
        ? prev.interests.filter(i => i !== area)
        : [...prev.interests, area]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/volunteers`, formData);
      setSubmitted(true);
      toast.success(t('volunteer.thankYou'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream pt-24 flex items-center justify-center" data-testid="volunteer-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-20 h-20 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-white" />
          </div>
          <h1 className="font-serif text-3xl text-charcoal mb-4">{t('volunteer.thankYou')}</h1>
          <p className="text-charcoal/70 mb-8">
            {t('volunteer.thankYouText')}
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: '', email: '', phone: '', interests: [], availability: '', message: '' });
            }}
            className="bg-burgundy hover:bg-purple text-white"
          >
            {t('volunteer.submitAnother')}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-24" data-testid="volunteer-page">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
              <Users size={32} className="text-white" />
            </div>
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('volunteer.joinTeam')}</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">{t('volunteer.title')}</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              {t('volunteer.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-6">
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gold/10"
            data-testid="volunteer-form"
          >
            <h2 className="font-serif text-2xl text-charcoal mb-6">{t('volunteer.interestForm')}</h2>
            
            <div className="space-y-6">
              {/* Personal Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('volunteer.fullName')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="bg-cream border-gold/20 focus:border-gold"
                    data-testid="volunteer-name"
                  />
                </div>
                <div>
                  <Label>{t('volunteer.phone')}</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="bg-cream border-gold/20 focus:border-gold"
                    data-testid="volunteer-phone"
                  />
                </div>
              </div>
              
              <div>
                <Label>{t('volunteer.email')}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="bg-cream border-gold/20 focus:border-gold"
                  data-testid="volunteer-email"
                />
              </div>

              {/* Areas of Interest */}
              <div>
                <Label className="mb-3 block">{t('volunteer.areasOfInterest')}</Label>
                <div className="grid grid-cols-2 gap-3">
                  {volunteerAreas.map((area) => (
                    <label
                      key={area.key}
                      className="flex items-center gap-3 p-3 bg-cream rounded-xl cursor-pointer hover:bg-gold/10 transition-colors"
                    >
                      <Checkbox
                        checked={formData.interests.includes(area.label)}
                        onCheckedChange={() => handleInterestChange(area.label)}
                        data-testid={`interest-${area.key}`}
                      />
                      <span className="text-sm text-charcoal">{area.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div>
                <Label>{t('volunteer.availability')}</Label>
                <Input
                  value={formData.availability}
                  onChange={(e) => setFormData({...formData, availability: e.target.value})}
                  placeholder={t('volunteer.availabilityPlaceholder')}
                  className="bg-cream border-gold/20 focus:border-gold"
                  data-testid="volunteer-availability"
                />
              </div>

              {/* Message */}
              <div>
                <Label>{t('volunteer.tellUsAbout')}</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder={t('volunteer.tellUsPlaceholder')}
                  rows={4}
                  className="bg-cream border-gold/20 focus:border-gold"
                  data-testid="volunteer-message"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-burgundy hover:bg-purple text-white py-4"
                data-testid="submit-volunteer"
              >
                {isSubmitting ? t('volunteer.submitting') : (
                  <>
                    <Heart size={18} className="mr-2" /> {t('volunteer.submitApplication')}
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </section>

      {/* Why Volunteer */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('volunteer.makeDifference')}</span>
            <h2 className="font-serif text-3xl text-charcoal">{t('volunteer.whyVolunteer')}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: t('volunteer.serveOthers'), desc: t('volunteer.serveOthersDesc') },
              { title: t('volunteer.growSpiritually'), desc: t('volunteer.growSpirituallyDesc') },
              { title: t('volunteer.buildCommunity'), desc: t('volunteer.buildCommunityDesc') },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-cream rounded-2xl"
              >
                <div className="w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart size={24} className="text-burgundy" />
                </div>
                <h3 className="font-serif text-lg text-charcoal mb-2">{item.title}</h3>
                <p className="text-charcoal/70 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
